import type { SetStateAction } from 'react';

import { chronicleLogger as logger } from '@/app-shell/chronicle/utils/chronicle-logger';
import type { ChronicleConnectionContext, ChronicleDeviceState } from '@/atoms/chronicle';

import type { StateEventResult } from './process-state-event';
import {
  logDevicePoweroffBatlow,
  trackDeviceDisconnected,
} from './track-connection-events';

export interface ApplyDisconnectSideEffectsParams {
  result: StateEventResult;
  linkedDeviceId: string | null;
  serialNumber: ChronicleDeviceState['serialNumber'];
  prevStatus: string;
  wasRecording: boolean;
  setConnectionContext: (update: SetStateAction<ChronicleConnectionContext>) => void;
}

/**
 * Logging + connection-context updates for a disconnect transition. A
 * deliberate low-battery power-off is reported distinctly from a link failure
 * and carries no auto-reconnect window, so it never drives the escalation
 * ladder.
 */
export function applyDisconnectSideEffects({
  result,
  linkedDeviceId,
  serialNumber,
  prevStatus,
  wasRecording,
  setConnectionContext,
}: ApplyDisconnectSideEffectsParams): void {
  if (!linkedDeviceId) return;

  if (result.isPoweroffBatlow) {
    logger.warn({
      msg: 'Chronicle low-battery power-off detected',
      deviceId: linkedDeviceId,
      serialNumber,
      prevStatus,
      newStatus: result.newStatus,
    });
    logDevicePoweroffBatlow({
      deviceId: linkedDeviceId,
      serialNumber,
      wasRecording,
      prevStatus,
      newStatus: result.newStatus,
    });
    // Warehouse sees the shutdown through the existing disconnect event; the
    // Datadog diagnostic above carries the operational detail the schema lacks.
    trackDeviceDisconnected({
      deviceId: linkedDeviceId,
      serialNumber,
      reason: 'poweroff_batlow',
    });
  } else if (result.isUnexpectedDisconnect) {
    logger.warn({
      msg: 'Chronicle unexpected disconnect detected',
      deviceId: linkedDeviceId,
      serialNumber,
      prevStatus,
      newStatus: result.newStatus,
      // HAR-1470: a drop during a recording and a drop while idle are different
      // incidents with the same log line today. Kept on the Datadog diagnostic
      // rather than the warehouse event, per the power-off branch above: the
      // Snowplow schema has no slot for it and adding one is a cross-repo
      // change (oscerai/snowplow Iglu) this does not need.
      wasRecording,
      autoReconnectUntil: result.connectionContextUpdate?.autoReconnectUntil,
    });
    trackDeviceDisconnected({
      deviceId: linkedDeviceId,
      serialNumber,
      reason: 'unexpected',
    });
  } else {
    return;
  }

  if (result.connectionContextUpdate) {
    setConnectionContext((ctx) => ({ ...ctx, ...result.connectionContextUpdate }));
  }
}
